import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectorRef, ChangeDetectionStrategy, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { JsonObjectFactoryService } from '../../../shared/_services/json-object-factory.service';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
  hydratedGlobalParams,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-f1120pc-sch-c',
    templateUrl: './f1120pc-sch-c.component.html',
    styleUrls: ['./f1120pc-sch-c.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class F1120pcSchCComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();
  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  // STANDARD COMPONENT PROPERTIES
  currentTabName = "Diagnostic";
  minimise = false;
  isSaveClicked = false;
  
  // Modal properties
  modalTitle = "Schedule C Dividends, Inclusions, Dividends-Received Deduction, and Other Special Deductions (see instructions)";
  showFormLoader = true;
  showDiagLoader = true;
  
  // Tab management
  activeTab = 1;
  tabs = [
    { id: 1, name: 'Diagnostic' },
    { id: 2, name: 'Form View' }
  ];

  // Component properties
  leId: any;
  cdr_no: any;
  leType: any;
  formName = '1120PC';
  hgt = '96%';
  systemFlag = 'D';

  // Form View data
  scheduleCFormView: any = { rows: [] };
  
  // Diagnostic data
  formData: any[] = [];
  accountData: any[] = [];
  totalLines: any[] = [];
  main_sub_data: any[] = [];
  acct_data: any[] = [];
  acctChild: any[] = [];
  
  no_acct_child = false;
  no_main_sub_data = false;
  show_toggle = true;
  show_toggle_total = true;
  no_total_lines = false;
  
  // Internal diagnostic state
  private currType: string = '';
  private main_indx: any = 'init';
  private main_sub_indx: any = 'init';
  private acct_indx: any = 'init';
  screen_key: string = ''; // Make it public so it can be set from filterParams
  private initialized = false;

  // Input properties
  rowData: any = {};
  colData: any = {};
  headerData: any = {};
  filterParams: any = {};
  baseUrls: any = {};
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};

  constructor(
    private jsonObjectFactory: JsonObjectFactoryService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.init();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.baseUrls = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header;
    this.USER_SETTINGS = header.userSettingsData || header.userSettings || {};
    this.SERVER_CONFIG = header.serverConfigData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.colData = activeScreen?.data?.colData || {};
    if (this.colData && Object.keys(this.colData).length > 0) {
      this.setDefaultTab();
    }
    this.screen_key =
      (header as any).screenKey ||
      this.filterParams?.screen_key ||
      this.globalService?.globalParams?.screen_key ||
      activeScreen?.link ||
      activeScreen?.screenKey ||
      '';
    this.globalService.filterParams = this.filterParams;
    this.initialized = false;
    this.cdr.markForCheck();
  }

  /**
   * Set default tab based on which column opened the modal
   * - If opened from "Diagnostic" column -> open tab 1 (Diagnostic)
   * - If opened from "Form View" column -> open tab 2 (Form View)
   */
  private setDefaultTab() {
    if (!this.colData || Object.keys(this.colData).length === 0) {
      console.log('❌ setDefaultTab: No colData available');
      return;
    }

    console.log('🔍 setDefaultTab: Full colData object:', JSON.stringify(this.colData, null, 2));
    
    // Try all possible property names that might contain column info
    const possibleNames = [
      this.colData.label,
      this.colData.field_name,
      this.colData.fieldName,
      this.colData.header_name,
      this.colData.headerName,
      this.colData.name,
      this.colData.field,
      this.colData.colId,
      this.colData.id
    ];

    console.log('🔍 Possible column names:', possibleNames);

    // Find first non-empty name
    let columnName = '';
    for (const name of possibleNames) {
      if (name && typeof name === 'string' && name.trim()) {
        columnName = name.toLowerCase();
        break;
      }
    }

    console.log('🔍 Selected column name:', columnName);

    // Check if column name contains "diagnostic"
    if (columnName.includes('diagnostic') || columnName.includes('diag')) {
      this.activeTab = 1;
      this.currentTabName = 'Diagnostic';
      console.log('✅ Opening Diagnostic tab (tab 1)');
    } 
    // Check if column name contains "form view"
    else if ((columnName.includes('form') && columnName.includes('view')) || 
             columnName.includes('formview') || 
             columnName.includes('form_view')) {
      this.activeTab = 2;
      this.currentTabName = 'Form View';
      console.log('✅ Opening Form View tab (tab 2)');
    }
    // Default to Diagnostic if unclear
    else {
      console.log('⚠️ Column name unclear, defaulting to Diagnostic tab. Column:', columnName);
      this.activeTab = 1;
      this.currentTabName = 'Diagnostic';
    }
  }

  init() {
    try {
      // Prevent multiple initializations
      if (this.initialized) {
        return;
      }

      // Wait for required data to be ready
      if (!this.globalService || !this.GENERAL_CONFIG || !this.GENERAL_CONFIG.base_url) {
        console.log('Waiting for globalService or GENERAL_CONFIG...');
        return;
      }

      if (!this.rowData || Object.keys(this.rowData).length === 0) {
        console.log('Waiting for rowData...');
        return;
      }

      // CRITICAL: Don't initialize if screen_key is not available
      if (!this.screen_key) {
        console.log('Waiting for screen_key from filterParams...');
        return;
      }

      // Mark as initialized to prevent duplicate API calls
      this.initialized = true;
      console.log('Schedule C initializing with screen_key:', this.screen_key);

      this.leId = this.rowData.LEID;
      this.cdr_no = this.rowData.MARS;
      this.leType = this.rowData.LE_TYPE;
      
      this.fetchScheduleCFormView();
      this.getAccts('parent', false, 1, '', 0, this.rowData.LINE_NO);
    } catch (e) {
      console.error('Error in F1120PC SCH C init:', e);
      this.showFormLoader = false;
      this.showDiagLoader = false;
    }
  }

  /**
   * Fetch Schedule C Form View data
   */
  fetchScheduleCFormView() {
    const params = {
      "action_id": 1245,
      "screen_key": this.screen_key,
      "tax_year": this.rowData.TAX_YEAR,
      "form_no": this.rowData.FORM_NO,
      "schd_name": this.rowData.SCHD_NAME,
      "scenario": this.rowData.SCENARIO,
      "jcd_key": this.rowData.JCD_KEY,
      "combination_key": this.rowData.COMBINATION_KEY
    };

    this.jsonObjectFactory.getJSONObj(this.GENERAL_CONFIG.base_url + '/loadJsonObject', params, this.globalService).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: any) => {
        console.log('Form View API response:', data);
        this.scheduleCFormView.rows = [];
        if (data.jsonObject && data.jsonObject.length > 0) {
          for (let i = 0; i < data.jsonObject.length; i++) {
            const tempDataObj: any = {};
            tempDataObj.num = data.jsonObject[i].LINE_NO;
            tempDataObj.description = data.jsonObject[i].LINE_DESCRIPTION;
            tempDataObj.value1 = data.jsonObject[i].A_SUB_TO_SEC_832;
            tempDataObj.value2 = data.jsonObject[i].B_DIV_AND_INC;
            tempDataObj.percent = data.jsonObject[i].C_PERCENT;
            tempDataObj.value3 = data.jsonObject[i].D_SUB_TO_SEC_832;
            tempDataObj.value4 = data.jsonObject[i].E_TOT_DIV_REC_SPECIAL_DED;
            
            this.scheduleCFormView.rows.push(tempDataObj);
          }
          console.log('✅ Form View data loaded - rows:', this.scheduleCFormView.rows.length, 'showFormLoader:', this.showFormLoader);
        } else {
          console.log('⚠️ No Form View data in response');
        }
        this.showFormLoader = false;
        console.log('Form View loader set to false. Current state:', this.showFormLoader);
        this.cdr.markForCheck();
      }, (error) => {
        console.error('❌ Error fetching Schedule C Form View:', error);
        this.showFormLoader = false;
        this.cdr.markForCheck();
      });
  }

  /**
   * Get accounts based on type and drill down level
   */
  getAccts(type: string, state: boolean, is_pt: number, acct: string, index: number, line_no: string) {
    this.no_main_sub_data = false;
    this.no_total_lines = false;
    this.currType = type;

    if (is_pt === 1) {
      this.main_indx = 'init';
      this.main_sub_indx = 'init';
      this.acct_indx = 'init';
    }

    if (this.main_indx === 'init' && type === 'main') {
      this.main_indx = index;
    } else if (this.main_sub_indx === 'init' && type === 'main_sub') {
      this.main_sub_indx = index;
    } else if (this.acct_indx === 'init' && type === 'accounts') {
      this.acct_indx = index;
    }

    if (this.currType === 'main') {
      if (!!this.formData[index].main_reload) {
        this.formData[index].toggleTotal = !state;
        this.formData[index].main_loader = true;
        this.formData[index].main_reload = 0;
        if (this.formData.length > 1 && this.main_indx !== index) {
          this.formData[this.main_indx].toggleTotal = false;
          this.formData[this.main_indx].main_reload = 1;
          this.totalLines = [];
          this.main_sub_data = [];
          this.acct_data = [];
          this.main_indx = index;
        }
      } else {
        this.formData[index].toggleTotal = !state;
        return;
      }
    } else if (this.currType === 'main_sub') {
      if (!!this.totalLines[index].main_sub_reload) {
        this.totalLines[index].toggle = !state;
        this.totalLines[index].main_sub_loader = true;
        this.totalLines[index].main_sub_reload = 0;
        if (this.totalLines.length > 1 && this.main_sub_indx !== index) {
          this.totalLines[this.main_sub_indx].toggle = false;
          this.totalLines[this.main_sub_indx].main_sub_reload = 1;
          this.main_sub_data = [];
          this.acct_data = [];
          this.main_sub_indx = index;
        }
      } else {
        this.totalLines[index].toggle = !state;
        return;
      }
    } else if (this.currType === 'accounts') {
      if (!!this.main_sub_data[index].child_reload) {
        this.main_sub_data[index].toggle = !state;
        this.main_sub_data[index].child_loader = true;
        this.main_sub_data[index].child_reload = 0;
        if (this.main_sub_data.length > 1 && this.acct_indx !== index) {
          this.main_sub_data[this.acct_indx].toggle = false;
          this.main_sub_data[this.acct_indx].child_reload = 1;
          this.acct_data = [];
          this.acct_indx = index;
        }
      } else {
        this.main_sub_data[index].toggle = !state;
        return;
      }
    } else if (this.currType === 'main_acct') {
      if (!!this.accountData[index].main_reload) {
        this.accountData[index].toggle = !state;
        this.accountData[index].main_loader = true;
        this.accountData[index].main_reload = 0;
      } else {
        this.accountData[index].toggle = !state;
        return;
      }
    }

    const params = {
      "action_id": 30098,
      "tax_year": this.rowData.TAX_YEAR,
      "screen_key": this.screen_key,
      "acct_key": acct,
      "combination_key": this.rowData.COMBINATION_KEY,
      "is_parent": is_pt,
      "line_no": line_no
    };

    this.jsonObjectFactory.getJSONObj(this.GENERAL_CONFIG.base_url + '/loadJsonObject', params, this.globalService).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: any) => {
        console.log('Diagnostic API response for type:', this.currType, 'data:', data);
        if (this.currType === 'parent' && data.jsonObject && data.jsonObject.length > 1) {
          if (data.jsonObject[1].TYPE === 'account') {
            this.showDiagLoader = false;
            this.formData.push(data.jsonObject[0]);
            this.formData[0].toggleTotal = false;
            this.formData[0].main_reload = 1;
            this.accountData.push(data.jsonObject[1]);
            this.accountData[0].toggle = false;
            this.accountData[0].main_reload = 1;
            this.accountData[0].main_loader = false;
            console.log('✅ Diagnostic data loaded (TYPE=account) - formData:', this.formData.length, 'accountData:', this.accountData.length);
          } else {
            this.createMapping(this.currType, index, data.jsonObject);
          }
        } else if (data.jsonObject) {
          this.createMapping(this.currType, index, data.jsonObject);
        } else {
          // No data returned
          this.showDiagLoader = false;
          console.log('⚠️ No diagnostic data returned');
        }
        console.log('Diagnostic state - formData:', this.formData.length, 'accountData:', this.accountData.length, 'showDiagLoader:', this.showDiagLoader);
        this.cdr.markForCheck();
      }, (error) => {
        console.error('❌ Error fetching accounts:', error);
        this.showDiagLoader = false;
        this.cdr.markForCheck();
      });
  }

  /**
   * Create mapping for diagnostic data based on type
   */
  private createMapping(currType: string, index: number, data: any[]) {
    if (currType === 'parent') {
      this.formData = data;
      this.formData.forEach((value, key) => {
        this.formData[key].toggleTotal = false;
        this.formData[key].main_reload = 1;
        this.formData[key].main_loader = false;
      });
      this.showDiagLoader = false;
      console.log('Diagnostic data loaded - formData:', this.formData.length, 'accountData:', this.accountData.length);
    } else if (currType === 'main') {
      if (data.length > 0) {
        this.formData[index].main_loader = false;
        this.totalLines = data;
        this.totalLines.forEach((value, key) => {
          this.totalLines[key].toggle = false;
          this.totalLines[key].main_sub_reload = 1;
          this.totalLines[key].main_sub_loader = false;
        });
        if (this.totalLines[0].TYPE === 'sys-form') {
          this.show_toggle_total = false;
        }
      } else {
        this.formData[index].main_loader = false;
        this.no_total_lines = true;
      }
    } else if (currType === 'main_sub') {
      if (data.length > 0) {
        this.main_sub_data = data;
        this.main_sub_data.forEach((value, key) => {
          this.main_sub_data[key].toggle = false;
          this.main_sub_data[key].child_reload = 1;
          this.main_sub_data[key].child_loader = false;
        });
        if (this.main_sub_data[0].TYPE === 'sys-form') {
          this.show_toggle = false;
        }
        this.totalLines[index].main_sub_loader = false;
      } else {
        this.no_main_sub_data = true;
        this.totalLines[index].main_sub_loader = false;
      }
    } else if (currType === 'main_acct') {
      if (data.length > 0) {
        this.acctChild = data;
        this.accountData[0].main_loader = false;
      } else {
        this.accountData[0].main_loader = false;
        this.no_acct_child = true;
      }
    } else {
      this.acct_data = data;
      this.main_sub_data[index].child_loader = false;
    }
  }

  /**
   * Check if input should be disabled based on row number
   */
  isDisabledForValue1(num: string): boolean {
    const disabledNums = ['10', '11', '12a', '12b', '12c', '13', '14', '15', '16', '17', '26', '27', '28', '29', '30'];
    return disabledNums.includes(num);
  }

  isDisabledForValue2(num: string): boolean {
    const disabledNums = ['26', '27', '28', '29', '30'];
    return disabledNums.includes(num);
  }

  isPercentHidden(num: string): boolean {
    const hiddenNums = ['10', '11', '12a', '12b', '12c', '13', '14', '15', '16', '17', '26', '27', '28', '30'];
    return hiddenNums.includes(num);
  }

  isValue3Hidden(num: string): boolean {
    const hiddenNums = ['10', '11', '12a', '12b', '12c', '13', '14', '15', '16', '17', '28', '29', '30'];
    return hiddenNums.includes(num);
  }

  isValue4Hidden(num: string): boolean {
    const hiddenNums = ['11', '12b', '12c', '13', '14', '15', '16', '17', '27'];
    return hiddenNums.includes(num);
  }

  /**
   * Format number with commas
   */
  formatNumber(value: number): string {
    if (value === null || value === undefined) {
      return '';
    }
    return value.toLocaleString('en-US');
  }

  cancel() {
    // CRITICAL: Use EventEmitter for AngularJS modal dismissal
    this.requestGridUpdate.emit('cancel');
  }
}

